<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useEmitter } from '../../composition/useEmitter'
import { UIButton, UIButtonGroup } from '@gohighlevel/ghl-ui'
import { Edit02Icon, Trash03Icon } from '@gohighlevel/ghl-icons/24/outline'
import {
  AltTypes,
  DigitalDelivery,
  EmitterKeys,
  MediaCenterType,
} from '../../types'

const emitter = useEmitter()
const emits = defineEmits(['click', 'select', 'edit', 'delete'])

const props = defineProps({
  triggerClassName: {
    type: String,
    default: '',
  },
  previewClassName: {
    type: String,
    default: '',
  },
  image: {
    type: String,
    default: '',
  },
  handshake: {
    type: Object,
    default: undefined,
    required: true,
  },
  companyId: {
    type: String,
    required: true,
    default: '',
  },
  locationId: {
    type: String,
    required: true,
    default: '',
  },
  altType: {
    type: String,
    default: AltTypes.LOCATION,
  },
  allowedExtensions: {
    type: Array,
    default: () => ['documents', 'image', 'video', 'audio'],
  },
  maxUploadSize: {
    type: Object,
    default: () => {
      return {}
    },
  },
  selfHosted: {
    type: Boolean,
    default: false,
  },
  contentType: {
    type: String,
    enum: MediaCenterType,
    required: false,
  },
})

function onSelectImage(image: any) {
  if (!image) return
  emits('select', image?.url)
}

const onClick = () => {
  openMediaLibrary()
  emits('click')
}

const onEditImage = () => {
  openMediaLibrary()
  emits('edit')
}

const onDeleteImage = () => {
  emits('delete')
}

const openMediaLibrary = () => {
  const settings = {
    companyId: props.companyId,
    altId: props.locationId,
    altType: props.altType,
    allowedExtensions: props.allowedExtensions,
    maxUploadSize: props.maxUploadSize,
    selfHosted: props.selfHosted,
    ...(props.contentType && { contentType: props.contentType }),
    contentAi: false,
    allowMultipleFileInsertion: false,
  }

  props.handshake.then((parent: any) => {
    parent.emit(EmitterKeys.OPEN_MEDIA_LIBRARY, settings)
  })
}

onUnmounted(() => {
  emitter.off(EmitterKeys.SELECTED_MEDIA_FILE)
})

onMounted(() => {
  emitter.on(EmitterKeys.SELECTED_MEDIA_FILE, (data: DigitalDelivery) => {
    onSelectImage(data)
  })
})
</script>

<template>
  <div @click="onClick" :class="props.triggerClassName" v-if="!props.image">
    <slot name="trigger"></slot>
  </div>
  <div v-if="props.image" :class="props.previewClassName">
    <div
      class="h-[120px] object-contain border-t border-l border-r border-1 border-gray-200 overflow-hidden p-2 flex items-center justify-center"
      style="
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        margin: auto;
      "
    >
      <img :src="props.image" alt="image" class="max-w-full" />
    </div>
    <div class="w-full h-full">
      <UIButtonGroup id="edit-image-button-group" class="flex">
        <UIButton ghost id="edit" @click="onEditImage">
          <Edit02Icon class="w-4 h-4" />
        </UIButton>
        <UIButton ghost id="delete" @click="onDeleteImage">
          <Trash03Icon class="w-4 h-4" />
        </UIButton>
      </UIButtonGroup>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#edit-image-button-group {
  .n-button {
    width: 50%;
    &:first-child {
      border-top-left-radius: 0px;
    }
    &:last-child {
      border-top-right-radius: 0px;
    }
  }
}
</style>
